import {
  AssetAssignmentStatusType,
  AssetDistributionStatusType,
  DistributionBatchType,
} from "./constants/common";
import { SystemUser } from "./constants/system-users";

export interface LoginUserTy {
  username: string;
  password: string;
}
export interface AuthUserTy {
  token_type: string;
  isSuccess: boolean;
  message?: string;
  isNewUser?: boolean;
  data?: {
    email?: string;
    is_active?: boolean;
  };
}
export interface UserVerifyTy extends Partial<BaseDoc> {
  email: string;
  user_category: string;
  first_name: string;
  last_name: string;
  is_verified: boolean | null;
  message: string;
  isNew?: boolean;
  domain_id?: string;
  role?: SystemUser;
}

export interface UserUpdatePasswordTy {
  email: string;
  password: string;
  confirmPassword?: string;
}
export interface CreateUsersTy {
  first_name: string;
  last_name: string;
  telephone: string;
  role_id: string;
  domain_id: string;
  user_category_id: string;
  user_domain: string;
  email: string;
}

export interface CreateUserResponse {
  message: string;
  data?: {
    id: string;
    created_at: string;
    updated_at: string;
    email: string;
    first_name: string;
    last_name: string;
    telephone: string;
    is_active: boolean;
    is_verified: boolean;
    role: string;
    user_level: string;
    region: string;
    facility: string;
  };
}

export interface BaseDoc {
  created_at: string;
  updated_at: string;
  id: string;
}
export interface Facilities extends BaseDoc {
  name: string;
  type: string;
  region: string;
}
export interface District extends BaseDoc {
  name: string;
  region: string;
  facilities: Facilities[];
}
export interface Region extends BaseDoc {
  name: string;
  districts: District[];
}

export interface GetUser extends BaseDoc {
  first_name: string;
  last_name: string;
  email: string;
  telephone: string;
  is_active: boolean;
  is_verified: boolean;
  role: string;
  role_id: string;
  user_category: string;
  user_category_id: string;
  user_domain: string;
  domain_id: string;
  domain_location: string;
}
export interface GetUserResponse {
  message: string;
  data: GetUser[];
}

export const fieldValidation = {
  EMIAL: "email",
  NUMBER: "number",
  BOOLEAN: "BOOLEAN",
  PASSWORD: "password",
  STRING: "string",
  TELEPHONE: "telephone",
  NONE: "none",
} as const;
export type FieldValidator =
  (typeof fieldValidation)[keyof typeof fieldValidation];
export interface ItemListTy {
  value: string;
  label: string;
}

export const assetType = {
  PHYSICAL: "Physical",
  VIRTUAL: "virtual",
} as const;

export type AssetType = (typeof assetType)[keyof typeof assetType];
export const assetTypeList = Object.values(assetType).map((entry) => entry);

export interface Permission {
  created_at: string;
  id: string;
  name: string;
}

export interface Roles extends BaseDoc {
  name: string;
  deleted_at: string | null;
  is_deleted: boolean;
  permissions: Permission[];
  permissions_count: number;
}
export interface IPagination {
  current_page: number | null;
  page_size: number | null;
  total_pages: number | null;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
export interface PaginationStateProps extends IPagination {
  processing?: {
    next?: boolean;
    previous?: boolean;
    pageNumber?: number;
  };
}
export interface ClientResponse<T> {
  message: string;
  data?: T;
  pagination?: IPagination;
  totalRecords?: number;
  success: boolean;
  extra?: any;
}

export type PageActions = {
  id?: string;
  name?: string;
  type?: "delete" | "add" | "edit";
  message?: string;
  title?: string;
  success?: boolean;
  data?: any;
};
export interface CreateFacility {
  name: string;
  district_id: string;
  region_id: string;
  type: string;
}

export interface Role {
  role_name: string;
  permission_ids?: string[];
}
export type AssetCategoryType = "physical" | "virtual";
export interface AssetCategory {
  name: string;
  asset_type?: AssetCategoryType;
}

export interface CreateAssetCategoryTag {
  asset_category_id: string;
  asset_tag_ids: string[];
}

export interface AccountTy {
  is_verified: boolean;
  is_active: boolean;
  isNewUser?: boolean;
}

interface AssetMeta {
  asset_category_tag_id: string;
  meta_value: string;
  asset_tag?: string;
}

export interface CreateAsset {
  serial_number: string;
  ownership: string;
  embossment_number?: string;
  model: string;
  brand: string;
  description: string;
  warranty_in_months?: number;
  asset_status: string;
  batch_id: string;
  asset_category_id: string;
  // asset_image_url: string;
  manufactured_date?: string;
  date_received: string;
  meta: AssetMeta[];
}

export interface CreateBatchInfo {
  donor_id: string;
  remarks: string;
  domain_id: string;
  batch_domain: string;
  received_date: string;
  quantity: number;
}

export interface AssetTag {
  asset_category_tag_id: string;
  name: string;
  meta_value?: string;
  asset_tag_field_type:
    | "text-input"
    | "number-input"
    | "single-select"
    | "multi-select";
  asset_tag_options?: string[];
}

export interface BaseAssetTag {
  name: string;
  asset_tag_field_type: string;
  asset_tag_options?: string[];
}

export interface CreateAssetTag extends BaseAssetTag {}

export interface UpdateAssetTag extends BaseAssetTag {}

export interface AssetTagResponse extends BaseDoc, BaseAssetTag {}

export type ListItem = {
  label: string;
  value: string;
  id?: string;
};

type AssetId = string;
export interface IAssetMaintenanceSchedule {
  service_date: string;
  service_remarks: string;
  schedule_status: string;
  assets: AssetId[];
}

export interface IAssetReport extends BaseDoc {
  issue: string;
  comments: string;
  resolution: string;
  evidence_images: IEvidenceImage[];
  assets: IAssetRecord[];
}

export interface IEvidenceImage extends BaseDoc {
  asset_report_id: string;
  object_key: string;
  file_type: string;
  file_size: number;
  signature_url: string;
}

export interface CreateAssetReport {
  issue: string;
  comments: string;
  resolution: string;
  asset_ids: string[];
}

export interface IAssetRecord extends BaseDoc {
  asset_category_id: string;
  asset_category_name: string;
  asset_status: string;
  assigned: boolean;
  batch_id: string;
  batch_number: string;
  brand: string;
  category_name: string;
  manufactured_date?: string; // ISO date format
  date_received: string; // ISO date format
  description: string;
  embossment_number?: string;
  model: string;
  serial_number: string;
  ownership: string;
  // asset_image_url: string;
  warranty_in_months?: number;
  asset_meta: AssetMeta[];
  current_location_domain: string;
  current_location: string;
  asset_meta_raw: any;
  assignment_status: AssetAssignmentStatusType;
  distribution_status: DistributionBatchType;
}
export type IAssetRecordKey = keyof IAssetRecord;

export interface ICreateAssetDistribution {
  from_location_id: string;
  from_location_domain: string;
  to_location_domain: string;
  to_location_id: string;
  assets: string[];
}
export interface ICreateAssetDispatch {
  asset_distribution_batches_ids: string[];
  courier_service_provider: string;
  package_description: string;
  dispatch_date: string;
  additional_remarks?: string;
  courier_transport_mode: string;
  vehicle_type: string;
  vehicle_number: string;
  name_of_courier: string;
  contact_of_courier: string;
}

export interface ICreateDistributionRejection {
  distribution_batch_id: string;
  user_id: string;
  reason: string;
}

export interface IDistributionBatchRecord extends BaseDoc {
  quantity: number;
  from_location: string;
  from_location_id: string;
  from_location_domain: string;
  to_location: string;
  to_location_id: string;
  to_location_domain: string;
  distribution_batch_status: DistributionBatchType;
}

export interface IBatch {
  batch_domain: string;
  batch_number: string;
  created_at: string;
  domain_id: string;
  donor: string;
  id: string;
  received_date: string;
  remarks: string;
  updated_at: string;
  quantity: number;
}

interface BaseQueryParams {
  page?: number;
  page_size?: number;
  is_deleted?: boolean;
}

export type IqueryParams<T = {}> = BaseQueryParams & T;
export interface IDistrict extends BaseDoc {
  name: string;
  region: string;
  region_id: string;
}
export interface IDivision extends BaseDoc {
  name: string;
}
export interface IRegion extends BaseDoc {
  name: string;
}
export interface IFacility extends BaseDoc {
  district: string;
  district_id: string;
  region_id: string;
  name: string;
  region: string;
  type: string;
}

export interface IUserCategory extends BaseDoc {
  name: string;
}
export interface IProgramme extends BaseDoc {
  division: string;
  division_id: string;
  name: string;
}

export interface AssetCategoryDetail extends BaseDoc {
  asset_type: AssetCategoryType;
  name: string;
}

export interface IQueryPerams {
  [key: string]: any;
}

export interface ICreateAssetAssignment {
  asset_id: string;
  user_id: string;
  remarks: string;
}
export interface IDonor extends BaseDoc {
  name: string;
}

export interface PieItem {
  id: string | number;
  value: number;
  label: string;
}

export interface DispatchRecord extends BaseDoc {
  courier_service_provider: string;
  vehicle_type: string;
  vehicle_number: string;
  name_of_courier: string;
  contact_of_courier: string;
  courier_transport_mode: string;
  dispatch_date: string;
  distribution_batch_id: string;
  distribution_batch_number: string;
  from_location: string;
  from_location_domain: string;
  id: string;
  package_description: string;
  additional_remarks?: string;
  to_location: string;
  to_location_domain: string;
}

export interface AssetAssignmentHistory {
  id: string;
  asset_category_id: string;
  asset_status: "functional" | "non-functional";
  assigned: boolean;
  batch_id: string;
  batch_number: string;
  brand: string;
  category_name: string;
  created_at: string;
  date_received: string;
  description: string | null;
  email: string;
  embossment_number: string;
  first_name: string;
  last_name: string;
  model: string;
  remarks: string;
  serial_number: string;
  telephone: string;
  updated_at: string;
  user_category: string;
  user_category_id: string;
  user_id: string;
  warranty_in_months: number;
}

export interface AssetDistributionQueryParams {
  asset_category_name?: string;
  asset_brand_name?: string;
  asset_distribution_status?: AssetDistributionStatusType;
}

export interface AssetAssignmentLog extends BaseDoc {
  asset_assignment_status: AssetAssignmentStatusType;
  asset_id: string;
  asset_brand: string;
  asset_model: string;
  asset_serial_number: string;
  asset_current_location: string;
  remarks?: string;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_category: string;
  user_domain: string;
  user_domain_location: string;
}

export interface AssetAssignmentRejectionLog extends BaseDoc {
  asset_assignment_id: string;
  reason: string;
  user_id: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_telephone: string;
  asset_id: string;
  asset_model: string;
  asset_brand: string;
  asset_serial_number: string;
  asset_embossment_number: string;
}

export interface AssetDistributionRejectionLog extends BaseDoc {
  distribution_batch_id: string;
  user_id: string;
  reason: string;
  distribution_batch_number: string;
  distribution_batch_status: string;
  distribution_batch_date: string;
  from_location: string;
  from_location_id: string;
  from_location_domain: string;
  to_location: string;
  to_location_id: string;
  to_location_domain: string;
  asset_id: string;
  asset_brand: string;
  asset_model: string;
  asset_serial_number: string;
  asset_embossment_number: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_telephone: string;
}

export interface UserQuery {
  telephone?: string;
  email?: string;
  user_category_id?: string;
  role_id?: string;
  is_deleted?: boolean;
}

export interface AlertItem {
  asset_serial_number: string;
  asset_category: string;
  service_date: string;
  service_remarks: string;
  schedule_status: string;
  days_remaining?: number;
  days_overdue?: number;
}
export interface AlertResponse {
  upcoming: AlertItem[];
  overdue: AlertItem[];
}

export interface CountClassifierMetric {
  data?: Record<string, number>;
  loading?: boolean;
}
